import LogRocketObject from 'logrocket';

import { IS_PRODUCTION, LOGROCKET_ID } from '../../config/Environment'; // oxlint-disable-line navina/restricted-syntax -- legacy env-name check; gate new features on a dedicated VITE__ flag instead (see .cursor/rules/environment.mdc)

type LogRocket = typeof LogRocketObject;
interface LogRocketModelProps {
	readonly logRocketInstance: LogRocket;
}

interface LogRocketIUserTraits {
	readonly [propName: string]: string | number | boolean;
}

type LogRocketICaptureOptions = NonNullable<Parameters<LogRocket['captureException']>[1]>;

interface ILogRocketModel {
	readonly logRocketInstance: LogRocket;

	readonly initialize: VoidFunction;
	readonly identify: (navinaUserId: string, traits: LogRocketIUserTraits) => void;
	readonly captureException: (error: Error, options?: LogRocketICaptureOptions) => void;
}

class LogRocketModel implements ILogRocketModel {
	readonly logRocketInstance: LogRocket;

	constructor({ logRocketInstance }: LogRocketModelProps) {
		if (!logRocketInstance) {
			throw new Error('LogRocket instance is required');
		}

		this.logRocketInstance = logRocketInstance;
	}

	static readonly create = (props: LogRocketModelProps): LogRocketModel => new LogRocketModel(props);

	readonly initialize = (): void => {
		const logrocketOptions: Parameters<LogRocket['init']>[1] = {
			release: __PACKAGE_VERSION__,
			console: {
				shouldAggregateConsoleErrors: true,
			},
			dom: {
				textSanitizer: true,
				inputSanitizer: 'lipsum',
			},
			network: {
				responseSanitizer(response) {
					if (response.headers['x-secret']) {
						// Removes all response data
						return null;
					}

					// Scrubs response body
					response.body = undefined;
					return response;
				},
			},
		};

		this.logRocketInstance.init(LOGROCKET_ID, logrocketOptions);
	};

	readonly identify = (navinaUserId: string, traits: LogRocketIUserTraits): void => {
		this.logRocketInstance.identify(navinaUserId, traits);
	};

	readonly captureException = (error: Error, options: LogRocketICaptureOptions): void => {
		this.logRocketInstance.captureException(error, options);
	};
}

let maybeLogRocketModel: ILogRocketModel | null = null;

export function getOrCreateInitializedLogRocketModel(): ILogRocketModel {
	if (!maybeLogRocketModel) {
		maybeLogRocketModel = LogRocketModel.create({ logRocketInstance: LogRocketObject });

		if (IS_PRODUCTION) {
			maybeLogRocketModel.initialize();
		}
	}

	return maybeLogRocketModel;
}
